import React, { useState, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useFetchDetail, usePostDetail } from "@hooks/useFetchDetail";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ServicesURL } from "@utils/utilsApi";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import AnimationFadeIn from "@components/AnimationFadeIn";
import CostProjectFormCst from "../CostProjectFormCst";
import { useAddEditCostProject } from "../hooks/useAddEditCostProject";
import { PagesID } from "@utils/utilsConfigurations";
import { isTrueValue } from "@utils/utilsValidators";

interface AddCostProjectInterface {
  onCancelAction: () => void;
  onSubmitAction: (data: any, list?: any) => void;
}

const AddCostProject: React.FC<AddCostProjectInterface> = ({
  onCancelAction,
  onSubmitAction,
}) => {
  const { t } = useTranslation(["projects-cost-page"]);
  const { idProject } = useParams();

  const { dataDetail: dataProject } = useFetchDetail(
    `${ServicesURL.getProjects}`,
    idProject,
  );

  const { columns } = useFetchDetail(
    ServicesURL.getProjectsCost,
    undefined,
    PagesID["projects.cost"],
  );

  const {
    handleAssociation,
    setOpenAssociation,
    handleDelete,
    getValues,
    resetCostProjectAssociations,
    setSupplierAssociation,
    setCategoryAssociation,
    setContractAssociation,
    supplierAssociation,
    categoryAssociation,
    contractAssociation,
    openAssociations,
  } = useAddEditCostProject([]);

  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    description?: string;
  }>();

  const { trigger: triggerProjectCost } = usePostDetail(
    `${ServicesURL.postProjectsCost}`,
  );

  const onSubmit = async (inputForm: any) => {
    setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
    const objToSend: any = {
      title: inputForm.title || null,
      iternal: inputForm.internal || null,
      budget_project_categorization_id:
        categoryAssociation && categoryAssociation.length > 0
          ? parseInt(categoryAssociation[0].id)
          : null,
      unit_cost: Number(inputForm.unit_cost) || null,
      qt: Number(inputForm.qt) || null,
      management_type:
        inputForm.management_type === "BC_BE" ||
        inputForm.management_type === "RDA"
          ? inputForm.management_type
          : null,
      supplier_id:
        supplierAssociation && supplierAssociation.length > 0
          ? parseInt(supplierAssociation[0].id)
          : null,
      contract_id:
        contractAssociation && contractAssociation.length > 0
          ? parseInt(contractAssociation[0].id)
          : null,
      funded: dataProject.dto?.funded ? isTrueValue(inputForm.funded) : null,
      person_days: Number(inputForm.person_days) || null,
      notes: inputForm.notes || null,
    };

    try {
      await triggerProjectCost(objToSend);
      setOpenToast({ s: StatusEnum.OK, message: "" });
    } catch (error) {
      setOpenToast(undefined);
      console.error(error);
    }
  };

  const getColumns = useMemo(() => {
    return columns.map((el) => {
      if (el.accessorKey === "funded") {
        return { ...el, disabled: !dataProject?.dto?.funded };
      } else {
        return { ...el };
      }
    });
  }, [dataProject, columns]);

  return (
    <AnimationFadeIn>
      {columns && (
        <Box
          sx={{
            mt: "25px",
            maxHeight: "100vh",
            overflowY: "auto",
          }}
        >
          <Typography
            variant="h3"
            component="h2"
            sx={{
              mb: "8px",
              textAlign: "center",
            }}
          >
            {t("hero-title")}
          </Typography>

          <CostProjectFormCst
            columns={getColumns}
            onSubmit={onSubmit}
            backButton={() => {
              onCancelAction();
            }}
            setOpenAssociation={(accessorKey, open, inputForm) =>
              setOpenAssociation(accessorKey, open, inputForm)
            }
            handleDelete={(accessorKey, el) => handleDelete(accessorKey, el)}
            handleAssociation={(accessorKey, value) =>
              handleAssociation(accessorKey, value)
            }
            getValues={(accessorKey) => getValues(accessorKey)}
            association={openAssociations}
            onReset={() => {
              setSupplierAssociation([]);
              setCategoryAssociation([]);
              setContractAssociation([]);
            }}
            resetCostProjectAssociations={resetCostProjectAssociations}
            setSupplierAssociation={setSupplierAssociation}
            setCategoryAssociation={setCategoryAssociation}
            setContractAssociation={setContractAssociation}
            categoryAssociation={categoryAssociation}
            supplierAssociation={supplierAssociation}
            dataProject={dataProject?.dto || []}
            fullWidth={true}
          />
        </Box>
      )}

      <ModalConfirmsCst
        open={!!openToast}
        title={t("hero-title-add")}
        description={""}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        callBackConfirmed={() => {
          setOpenToast(undefined);
          onCancelAction();
          onSubmitAction(undefined, undefined);
        }}
        status={openToast}
      />
    </AnimationFadeIn>
  );
};

export default AddCostProject;
